<script>
  export let image;
  export let imageAlt = "Popup Image";
  export let title;
  export let content;
  export let videoUrl;
  export let socialVideo = null;
  export let gallery;
  export let handleVideoLoad;
  export let handleSocialVideoLoad;
  export let directions;
  /** From hotspot editor / tour JSON `galleryZoomMinimap` or krpano `gallery_zoom_minimap` */
  export let enableGalleryZoomMinimap = false;

  import UAParser from "ua-parser-js";
  let parser = new UAParser();
  let os = parser.getOS();
  let isMobile = parser.getResult().device["type"] == "mobile";

  import IconDirections from "./IconDirections.svelte";
  import IconCar from "./IconCar.svelte";
  import IconBuilding from "./IconBuilding.svelte";

  import CloseButton from "./CloseButton.svelte";
  import PopupGallery from "./PopupGallery.svelte";
  import { fly, fade } from "svelte/transition";
  import { focusTrap, iconifyDirectionIconUrl } from "./utils.js";
  import { tourFunctions } from "../tour-functions";
  import { tick } from "svelte";
  export let ariaLabel = `Popup: ${title}`;

  let popupModal;
  let scrollContainer;
  let popupContentEl;

  $: if (visible) focusPopup();

  // Create a focus trap
  async function focusPopup() {
    await tick();
    if (popupContentEl) {
      popupContentEl.scrollTop = 0;
    }
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
      scrollContainer.focus({ preventScroll: true });
    }
  }

  function openGallery() {
    const galleryElem = document.getElementById("popup-gallery");
    new PopupGallery({
      target: galleryElem,
      props: {
        gallery: gallery,
        close: closePopup,
        enableZoomMinimap: enableGalleryZoomMinimap,
      },
    });
    focusTrap(galleryElem);
  }

  let visible = true;
  function closePopup() {
    visible = false;
    tourFunctions.focus();
    
    // Clear hotspot hash if it exists
    if (window.location.hash.startsWith('#/hotspot/')) {
      // Clear the hash by setting it to empty
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }

  function playVideo() {
    if (socialVideo?.url) {
      handleSocialVideoLoad?.();
    } else if (videoUrl) {
      handleVideoLoad?.();
    }
  }

  $: hasVideo = Boolean(videoUrl || socialVideo?.url);

  function getPopupTextColor() {
    // get --popup-text-color from the root element
    const rootStyles = getComputedStyle(document.documentElement);
    const popupTextColor = rootStyles.getPropertyValue("--popup-text-color");
    return popupTextColor || "#000";
  }

  function generateMapUrl(location, transportation) {
    if (!location) return;

    const baseUrl = "https://www.google.com/maps/dir/";
    // Handle both old format (array) and new format (object with lat, long)
    let coordinates;
    if (Array.isArray(location)) {
      const a = Number(location[0]);
      const b = Number(location[1]);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return;
      coordinates = `${a},${b}`;
    } else if (
      location.lat != null &&
      location.long != null &&
      Number.isFinite(Number(location.lat)) &&
      Number.isFinite(Number(location.long))
    ) {
      coordinates = `${location.lat},${location.long}`;
    } else {
      return;
    }

    if (os.name === "iOS") {
      return `http://maps.apple.com/?daddr=${coordinates}&t=s`;
    } else {
      let travelMode;

      if (transportation === "walking") {
        travelMode = "data=!3m1!4b1!4m2!4m1!3e2";
      } else if (transportation === "driving") {
        travelMode = "data=!4m2!4m1!3e0";
      } else {
        travelMode = "";
      }

      return `${baseUrl}${coordinates}/@${coordinates},12z/${travelMode}`;
    }
  }

  // Handle multiple directions with different types
  function getDirectionLabel(direction) {
    const trimmed = direction.label?.trim();
    if (trimmed) return trimmed;
    if (direction.type === "parking") return "Parking Directions";
    if (direction.type === "building") return "Building Directions";
    return "Directions";
  }

  $: directionButtons =
    directions && directions.length > 0
      ? directions.map((direction) => ({
          url: generateMapUrl(direction, "driving"),
          type: direction.type,
          label: getDirectionLabel(direction),
          iconUrl:
            direction.iconUrl ||
            (direction.type === "custom" && direction.icon
              ? iconifyDirectionIconUrl(direction.icon)
              : null),
          coordinates: `${direction.lat}, ${direction.long}`,
        }))
      : [];

  // Function to copy coordinates to clipboard
  async function copyToClipboard(text, buttonElement) {
    try {
      await navigator.clipboard.writeText(text);
      
      // Store original text
      const originalText = buttonElement.textContent;
      
      // Change button text to show success
      buttonElement.textContent = 'Copied!';
      buttonElement.style.background = '#fff';
      buttonElement.style.color = '#000';
      
      // Reset after 2 seconds
      setTimeout(() => {
        buttonElement.textContent = originalText;
        buttonElement.style.background = '';
        buttonElement.style.color = '';
      }, 2000);
      
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      
      // Show error feedback
      const originalText = buttonElement.textContent;
      buttonElement.textContent = 'Failed';
      buttonElement.style.background = '#ff4444';
      buttonElement.style.color = '#fff';
      
      // Reset after 2 seconds
      setTimeout(() => {
        buttonElement.textContent = originalText;
        buttonElement.style.background = '';
        buttonElement.style.color = '';
      }, 2000);
    }
  }
</script>

{#if visible}
  <div id="popup-gallery"></div>

  <div
    bind:this={popupModal}
    class="popup-modal"
    class:has-image={image ? true : false}
    use:focusTrap
  >
    <div class="popup-area">
      <div class="popup-window-wrap">
        <div class="popup-window" transition:fly={{ y: 800, duration: 500 }}>
          <div class="popup-layout">
            <div class="popup-content" bind:this={popupContentEl}>
              <div class="popup">
              {#if image}
                <div class="image column">
                  <img src={image} alt={imageAlt} class="popup-image" />
                  <div class="items">
                    {#if gallery}
                      <button
                        class="gallery item"
                        tabindex="0"
                        on:click={openGallery}
                        aria-label="Open Gallery"
                      >
                        <!-- <img
                          src="./images/popup_gallery_button.png"
                          alt="Open Gallery"
                        /> -->
                        <div class="icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 197.04 197.04"
                            ><circle
                              cx="98.52"
                              cy="98.52"
                              r="88.52"
                              style="fill:none;stroke:var(--popup-text-color);stroke-miterlimit:10;stroke-width:15px"
                            /><path
                              d="M145,67.57H127.41l-2-6.64a5.17,5.17,0,0,0-5-3.68H79.66a5.15,5.15,0,0,0-4.94,3.68l-2,6.64H52.09a5.16,5.16,0,0,0-5.16,5.15v61.91a5.16,5.16,0,0,0,5.16,5.16H145a5.16,5.16,0,0,0,5.16-5.16V72.72A5.16,5.16,0,0,0,145,67.57ZM98.52,119.15a20.64,20.64,0,1,1,20.63-20.63A20.64,20.64,0,0,1,98.52,119.15Z"
                              style="fill:var(--popup-text-color);"
                            /></svg
                          >
                        </div>
                        <span>Photos</span>
                      </button>
                    {/if}
                    {#if hasVideo}
                      <button
                        class="video item"
                        tabindex="0"
                        on:click={playVideo}
                        aria-label="Play Video"
                      >
                        <!-- <img
                          src="./images/popup_video_button.png"
                          alt="Play Video"
                        /> -->
                        <div class="icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 197.04 197.04"
                            ><path
                              d="M61,98.14c0-13.83,0-27.65,0-41.47,0-9.45,6.43-13.08,14.69-8.26q35.57,20.78,71,41.69c8.17,4.82,8.16,11.62,0,16.44Q111.52,127.33,76.16,148c-8.68,5.07-15.09,1.26-15.11-8.88C61,125.45,61,111.79,61,98.14Z"
                              style="fill:var(--popup-text-color)"
                            /><circle
                              cx="98.52"
                              cy="98.52"
                              r="88.52"
                              style="fill:none;stroke:var(--popup-text-color);stroke-miterlimit:10;stroke-width:15px"
                            /></svg
                          >
                        </div>
                        <span>Video</span>
                      </button>
                    {/if}
                  </div>
                </div>
              {/if}

              <div class="content column">
                <!-- svelte-ignore a11y-no-noninteractive-tabindex -->
                <div
                  class="content-inner"
                  role="dialog"
                  aria-label={ariaLabel}
                  tabindex="0"
                  bind:this={scrollContainer}
                >
                  <h2 class="title">{@html title}</h2>
                  <!-- Directions Links -->
                  {#if directionButtons.length > 0}
                    <div class="directions-container">
                      {#each directionButtons as direction}
                        {#if direction.url}
                          <div class="direction-row">
                            <a 
                              class="directions-link" 
                              href={direction.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Get {direction.label.toLowerCase()}"
                            >
                              <div class="icon-container">
                                {#if direction.type === "parking"}
                                  <IconCar size={"2em"} fill="#fff" />
                                {:else if direction.type === "building"}
                                  <IconBuilding size={"2em"} fill="#fff" />
                                {:else if direction.iconUrl}
                                  <img
                                    class="directions-iconify-img"
                                    src={direction.iconUrl}
                                    alt=""
                                  />
                                {:else}
                                  <IconDirections size={"2em"} fill="#fff" />
                                {/if}
                              </div>
                              <span>{direction.label}</span>
                            </a>
                            <button 
                              class="copy-gps-button" 
                              on:click={(e) => copyToClipboard(direction.coordinates, e.target)}
                              aria-label="Copy GPS coordinates: {direction.coordinates}"
                            >
                              Copy GPS
                            </button>
                          </div>
                        {/if}
                      {/each}
                    </div>
                  {/if}
                  {@html content}
                  {#if !image}
                    <div class="items">
                      {#if gallery}
                        <button
                          class="gallery item"
                          tabindex="0"
                          on:click={openGallery}
                          aria-label="Open Gallery"
                        >
                          <div class="icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 197.04 197.04"
                              ><circle
                                cx="98.52"
                                cy="98.52"
                                r="88.52"
                                style="fill:none;stroke:currentColor;stroke-miterlimit:10;stroke-width:15px"
                              /><path
                                d="M145,67.57H127.41l-2-6.64a5.17,5.17,0,0,0-5-3.68H79.66a5.15,5.15,0,0,0-4.94,3.68l-2,6.64H52.09a5.16,5.16,0,0,0-5.16,5.15v61.91a5.16,5.16,0,0,0,5.16,5.16H145a5.16,5.16,0,0,0,5.16-5.16V72.72A5.16,5.16,0,0,0,145,67.57ZM98.52,119.15a20.64,20.64,0,1,1,20.63-20.63A20.64,20.64,0,0,1,98.52,119.15Z"
                                style="fill:currentColor;"
                              /></svg
                            >
                          </div>
                          <span>Photos</span>
                        </button>
                      {/if}
                      {#if hasVideo}
                        <button
                          class="video item"
                          tabindex="0"
                          on:click={playVideo}
                          aria-label="Play Video"
                        >
                          <div class="icon">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 197.04 197.04"
                              ><path
                                d="M61,98.14c0-13.83,0-27.65,0-41.47,0-9.45,6.43-13.08,14.69-8.26q35.57,20.78,71,41.69c8.17,4.82,8.16,11.62,0,16.44Q111.52,127.33,76.16,148c-8.68,5.07-15.09,1.26-15.11-8.88C61,125.45,61,111.79,61,98.14Z"
                                style="fill:currentColor"
                              /><circle
                                cx="98.52"
                                cy="98.52"
                                r="88.52"
                                style="fill:none;stroke:currentColor;stroke-miterlimit:10;stroke-width:15px"
                              /></svg
                            >
                          </div>
                          <span>Video</span>
                        </button>
                      {/if}
                    </div>
                    <!-- END .items -->
                  {/if}
                </div>
                <!-- end content-inner -->
              </div>
              <!-- end content -->
              </div>
            </div>
            <div class="close-button-container">
              <CloseButton
                close={closePopup}
                color="#ffffff"
                useAbsolutePosition={false}
                marginBottom={isMobile ? "0" : "1rem"}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        class="popup-bg"
        on:click={closePopup}
        transition:fade={{ duration: 500 }}
      ></div>
    </div>
  </div>
{/if}

<style>
  .popup {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .column {
    display: flex;
    flex-direction: column;
    flex-basis: 100%;
    flex: 1;
    height: 100%;
    min-height: 0;
    min-width: 0;
  }

  .image {
    background-repeat: no-repeat;
    background-size: cover;
    position: relative;
  }

  .title {
    margin: 2rem 0;
    margin-bottom: 0;
    font-family: var(--font-heading), sans-serif;
    font-size: 2rem;
    line-height: 1;
  }

  .content {
    justify-content: center;
    font-family: var(--font-body), sans-serif;
    line-height: 1.5;
  }

  .content-inner {
    padding: 2rem;
    padding-top: 0;
    overflow-y: scroll;
    max-height: 90vh;
  }

  .content-inner:focus {
    outline: none;
  }

  .items {
    position: absolute;
    width: 100%;
    bottom: 0;
    left: 0;
    right: 0;
    box-sizing: border-box;

    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 5%;
    justify-content: center;
  }

  .item {
    appearance: none;
    border: none;
    text-align: inherit;
    color: inherit;

    cursor: pointer;
    background: rgba(var(--popup-bg-color-rgb), 0.85);
    border-radius: 5px;
    margin: 5px;
    padding: 0.5rem 2rem;
    font-size: 1.5rem;
    display: flex;
    flex-direction: row;
    flex-basis: 100%;
    flex: 0;
    height: 100%;
    font-family: var(--font-heading), sans-serif;
    font-weight: 300;
    box-shadow: 1px 2px 2px #00000040;
    justify-content: center;
    align-items: center;
    border: 2px solid transparent;
  }

  .item:hover {
    border: 2px solid var(--popup-text-color);
  }

  .item img,
  .item svg {
    width: 1em;
    height: 1em;
  }

  .icon {
    margin-inline: 0.25em;
    margin-top: 0.15em;
  }

  :global(.popup .content-inner a) {
    color: var(--popup-text-color);
    font-weight: 700;
  }

  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-thumb {
    background: var(--popup-text-color);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--popup-text-color);
  }

  .popup-area {
    position: fixed;
    z-index: 2001;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }

  .popup-bg {
    position: fixed;
    z-index: 2000;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.66);
    backdrop-filter: blur(2px);
  }

  .popup-window-wrap {
    position: relative;
    display: flex;
    justify-content: center;
    width: fit-content;
    max-width: calc(100vw - 4rem);
    margin: 2rem auto;
    max-height: calc(100vh - 4rem);
    box-sizing: border-box;
  }

  .popup-modal.has-image .popup-window-wrap {
    width: 100%;
  }

  .popup-layout {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    max-height: 95vh;
    max-width: 2000px;
    margin: auto;
  }

  .close-button-container {
    flex-shrink: 0;
  }

  .popup-window {
    position: relative;
    z-index: 99999;
    background: transparent;
    width: auto;
    max-width: 95vw;
    max-height: 95vh;
    margin: 0;
    overflow: visible;
  }

  .popup-content {
    flex: 0 1 auto;
    border-radius: 10px;
    background: var(--popup-bg-color);
    color: var(--popup-text-color);
    overflow: hidden;
    width: 40rem;
    max-width: calc(100vw - 4rem - 50px - 1rem);
    max-height: 95vh;
    padding: 0;
  }

  @media only screen and (min-width: 901px) {
    .popup-modal.has-image .popup-window-wrap {
      width: fit-content;
      max-width: calc(100vw - 4rem);
    }

    .popup-modal.has-image .popup-layout {
      max-height: calc(100vh - 4rem);
      max-width: min(
        2000px,
        calc(100vw - 4rem - 50px - 1rem),
        calc((100vh - 4rem) * 20 / 9)
      );
      min-height: 0;
    }

    .popup-modal.has-image .popup-window {
      width: auto;
      max-width: min(
        2000px,
        calc(100vw - 4rem - 50px - 1rem),
        calc((100vh - 4rem) * 20 / 9)
      );
      max-height: calc(100vh - 4rem);
      overflow: hidden;
    }

    .popup-modal.has-image .popup-content {
      flex: 1 1 auto;
      position: relative;
      width: min(
        2000px,
        calc(100vw - 4rem - 50px - 1rem),
        calc((100vh - 4rem) * 20 / 9)
      );
      aspect-ratio: 20 / 9;
      max-height: calc(100vh - 4rem);
      height: auto;
      padding-bottom: 0;
    }

    .popup-modal.has-image .popup-content .popup {
      position: absolute;
      inset: 0;
    }

    .popup-modal.has-image .content-inner {
      max-height: 100%;
      min-height: 0;
    }
  }

  .popup-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .content-inner .items {
    position: static;
    padding: 0;
  }
  .content-inner .item {
    background: #fff;
    color: #000;
  }

  .directions-link {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    font-weight: 500;
    background: none;
    border: none;
    color: #fff;
    text-decoration: underline;
    padding: 0;
    margin-top: 1rem;
  }

  .directions-link:hover,
  .directions-link:hover span {
    font-weight: bold;
  }

  .directions-link:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px var(--focus-color);
    border-radius: 4px;
  }

  .directions-link span {
    margin: 0 0.5em;
    font-weight: 500;
  }

  .icon-container {
    width: 1.5em;
    height: 1.5em;
    text-align: center;
    display: inline-flex;
  }

  .directions-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .directions-container .directions-link {
    margin-top: 0;
  }

  .direction-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .copy-gps-button {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    font-weight: 500;
    background: none;
    border: 2px solid #fff;
    border-radius: 2em;
    color: #fff;
    text-decoration: none;
    padding: 0.25rem 0.5rem;
    font-size: 0.75em;
    white-space: nowrap;
    transition: all 0.3s ease;
    min-width: 60px;
    justify-content: center;
  }

  .copy-gps-button:hover {
    background: #fff;
    color: #000;
  }

  .copy-gps-button:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px var(--focus-color);
    border-radius: 4px;
  }

  @media only screen and (max-width: 900px) {
    .popup-area {
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      justify-content: center;
      align-items: center;
      overflow: hidden;
    }

    .popup-window-wrap {
      width: min(500px, calc(100vw - 2rem)) !important;
      max-width: min(500px, calc(100vw - 2rem));
      max-height: calc(100dvh - 2rem);
      height: auto;
      margin: auto !important;
      padding: 0 !important;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .popup-window {
      width: 100% !important;
      max-width: 100% !important;
      max-height: 100%;
      height: auto;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .popup-content {
      width: 100%;
      max-width: 100%;
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
    }

    .popup-modal.has-image .popup-content {
      width: 100%;
      max-width: 100%;
      aspect-ratio: auto;
      height: auto;
      max-height: none;
      padding-bottom: 0;
      overflow: hidden;
    }

    .popup-modal.has-image .popup-window {
      max-width: 100% !important;
      max-height: calc(100dvh - 2rem);
    }

    .popup-modal.has-image .popup-window-wrap {
      width: min(500px, calc(100vw - 2rem)) !important;
      max-width: min(500px, calc(100vw - 2rem));
    }

    .popup-modal.has-image .popup-content .popup {
      position: static;
    }

    .popup-modal.has-image .content-inner {
      overflow-y: auto;
      overflow-x: hidden;
      -webkit-overflow-scrolling: touch;
      /* viewport minus 4:3 image, close btn, and gaps */
      max-height: max(4rem, calc(100dvh - min(500px, 100vw - 2rem) * 3 / 4 - 5.5rem));
      min-height: 0;
    }

    .has-image .popup-window-wrap,
    .has-image .popup-window {
      max-height: calc(100dvh - 2rem);
      height: auto !important;
    }

    .column {
      width: 100%;
      flex: none;
    }

    .has-image .column {
      height: auto;
      width: 100%;
      flex: none;
    }

    .image.column {
      min-height: 40%;
      height: fit-content;
      aspect-ratio: 4/3;
    }

    .content {
      justify-content: flex-start;
    }

    .title {
      font-size: 1.75em;
    }

    .content-inner {
      font-size: 14px;
      height: auto;
      max-height: none;
      overflow-y: visible;
      padding: 1.25rem;
      padding-top: 0;
    }

    .popup-layout {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
      max-height: calc(100dvh - 2rem);
      max-width: min(500px, calc(100vw - 2rem));
      min-height: 0;
      flex: 1 1 auto;
      width: 100%;
      margin: 0 auto;
    }

    .close-button-container {
      flex-shrink: 0;
      align-self: center;
    }

    .popup {
      height: auto;
      flex-direction: column;
    }

    .items {
      padding: 0.25rem;
    }

    .item {
      padding: 0.5rem 1.25rem;
    }

    .direction-row {
      flex-direction: column;
      align-items: stretch;
      margin-bottom: 1rem;
    }

    .directions-link {
      font-size: 1.25em;
    }

    .copy-gps-button {
      padding: 0.75rem 2rem;
      font-size: 0.8em;
      justify-content: center;
      display: none;
    }
  }

  /* Tablet portrait (iPad mini, iPad Air): compact stacked layout */
  @media only screen and (min-width: 601px) and (max-width: 900px) {
    .popup-window-wrap {
      width: min(500px, calc(100vw - 1.5rem)) !important;
      max-width: min(500px, calc(100vw - 1.5rem));
      max-height: calc(100dvh - 1.5rem);
    }

    .popup-modal.has-image .popup-window-wrap {
      width: min(500px, calc(100vw - 1.5rem)) !important;
      max-width: min(500px, calc(100vw - 1.5rem));
    }

    .popup-layout {
      gap: 0.375rem;
      max-height: calc(100dvh - 1.5rem);
      max-width: min(500px, calc(100vw - 1.5rem));
    }

    .has-image .popup-window-wrap,
    .has-image .popup-window {
      max-height: calc(100dvh - 1.5rem);
    }

    .popup-modal.has-image .popup-window {
      max-height: calc(100dvh - 1.5rem);
    }

    .image.column {
      aspect-ratio: 16 / 9;
    }

    .title {
      font-size: 1.5rem;
      margin: 1rem 0 0;
    }

    .content-inner {
      padding: 1rem;
      padding-top: 0;
    }

    .popup-modal.has-image .content-inner {
      max-height: max(4rem, calc(100dvh - min(500px, 100vw - 1.5rem) * 9 / 16 - 4rem));
    }

    .item {
      padding: 0.4rem 1rem;
      font-size: 1.25rem;
    }

    .items {
      padding: 0.2rem;
    }
  }
</style>
